import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ConnectorCard,
  AlertCard,
  PageViewerControls,
  toast,
  activityRail,
  KpiHeroBand,
  StatusDonutMini,
  GaugeCard,
  StaggerReveal,
} from '../../components/index.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { RESOURCE_AREAS, buildResourceEcosystemSummary } from './resourceEcosystemSignals.js';
import './admin.css';
import './dataIntegration.redesign.css';

// Fleet-health classification: maps a connector status string to one of three
// executive-traffic-light buckets so the donut + KPIs stay data-driven.
function fleetStateFor(status) {
  const s = String(status || '').toLowerCase();
  if (/error|fail|down|offline/.test(s)) return 'down';
  if (/warn|degrad|latency|stale/.test(s)) return 'degraded';
  if (/healthy|ok|connected|ready|nominal/.test(s)) return 'ready';
  return 'degraded';
}

const STATE_LABEL = { ready: 'Ready', degraded: 'Degraded', down: 'Down' };
const STATE_TONE = { ready: 'sage', degraded: 'amber', down: 'brick' };

// Pull whatever health telemetry the seed payload carries. The connectors API
// returns the full payload, so availability / latency / open-issue counts are
// present in production data but absent in minimal test fixtures — every reader
// is null-safe so the page degrades cleanly when health is missing.
function readHealth(connectors) {
  const avail = [];
  const latencies = [];
  let openIssues = 0;
  let issuesKnown = false;
  for (const c of connectors) {
    const h = c.health || {};
    if (typeof h.availability === 'number') avail.push(h.availability);
    if (typeof h.latencyMsP95 === 'number') latencies.push(h.latencyMsP95);
    if (typeof h.openIssueCount === 'number') {
      openIssues += h.openIssueCount;
      issuesKnown = true;
    }
  }
  const avgAvail = avail.length ? avail.reduce((a, b) => a + b, 0) / avail.length : null;
  const worstLatency = latencies.length ? Math.max(...latencies) : null;
  return {
    hasAvailability: avail.length > 0,
    avgAvailabilityPct: avgAvail == null ? null : Math.round(avgAvail * 1000) / 10,
    hasLatency: latencies.length > 0,
    worstLatencyMs: worstLatency,
    issuesKnown,
    openIssues,
  };
}

function advancedPrototypeCoveragePct(connectors) {
  if (!connectors.length) return null;
  const live = connectors.filter((c) =>
    /read-only-api|production-authorized/.test(String(c.maturityLevel || '')),
  ).length;
  return Math.round((live / connectors.length) * 1000) / 10;
}

export function DataIntegrationRbacSettingsPage() {
  const conns = useApi('/connectors', { query: { limit: 50 } });
  // The audit feed is readable only by roles that can open /admin/audit —
  // requesting it for anyone else just produces a silent 403 (OBS-001).
  const { roles: actorRoles } = useCurrentUser();
  const canReadAudit = rbac.hasAnyRole(actorRoles, rbac.ROUTE_PERMISSIONS['/admin/audit'] || []);
  const audit = useApi('/audit-events', { query: { limit: 20 }, skip: !canReadAudit });
  const syncRunsRes = useApi('/sync-runs', { query: { limit: 200 } });
  const sync = useMutation();
  const navigate = useNavigate();
  const [unhealthyFirst, setUnhealthyFirst] = useState(false);
  const connectors = useMemo(() => conns.data?.data || [], [conns.data]);
  const auditList = useMemo(() => audit.data?.data || [], [audit.data]);
  const orderedConnectors = useMemo(() => {
    if (!unhealthyFirst) return connectors;
    const rank = (c) => (/warn|error|fail|degrad|down|offline/i.test(c.status || '') ? 0 : 1);
    return [...connectors].sort((a, b) => rank(a) - rank(b));
  }, [connectors, unhealthyFirst]);
  const connectorPager = useClientPagination(orderedConnectors, {
    initialPageSize: 8,
    resetKey: `connectors-${connectors.length}-${unhealthyFirst ? 'unhealthy' : 'default'}`,
  });
  const auditPager = useClientPagination(auditList, {
    initialPageSize: 5,
    resetKey: `audit-${auditList.length}`,
  });
  const ecosystemSummary = useMemo(
    () => buildResourceEcosystemSummary(connectors, { auditCount: auditList.length }),
    [connectors, auditList],
  );

  usePageMetadata({
    title: 'Connectors',
    subtitle: 'Prototype data adapters, resource domains, and simulated diagnostics for DHA PRIME.',
  });

  // Per-connector simulation-history series (projected records per run). A
  // connector with no recorded runs gets no sparkline (AC-TRUST-2).
  const histories = useMemo(() => {
    const map = {};
    for (const r of syncRunsRes.data?.data || []) {
      if (!r.connectorId) continue;
      (map[r.connectorId] ||= []).push(r);
    }
    for (const id of Object.keys(map)) {
      map[id] = map[id]
        .sort((a, b) => new Date(a.startedAt || 0) - new Date(b.startedAt || 0))
        .slice(-14)
        .map((r) => Number(r.projectedRecords ?? r.recordsProcessed ?? 0));
    }
    return map;
  }, [syncRunsRes.data]);

  // Fleet-health rollup — derived purely from already-fetched connector status
  // and the health telemetry the previous page discarded.
  const fleet = useMemo(() => {
    const counts = { ready: 0, degraded: 0, down: 0 };
    for (const c of connectors) counts[fleetStateFor(c.status)] += 1;
    return counts;
  }, [connectors]);
  const health = useMemo(() => readHealth(connectors), [connectors]);
  const maturityPct = useMemo(() => advancedPrototypeCoveragePct(connectors), [connectors]);

  const donutSegments = useMemo(
    () =>
      ['ready', 'degraded', 'down'].map((k) => ({
        label: STATE_LABEL[k],
        count: fleet[k],
        tone: STATE_TONE[k],
      })),
    [fleet],
  );

  async function simulateSync(c) {
    try {
      const res = await sync.run(() => api.post(`/connectors/${c.id}/sync`, {}));
      conns.data &&
        (conns.data.data = conns.data.data.map((x) =>
          x.id === c.id ? { ...x, lastSyncAt: new Date().toISOString() } : x,
        ));
      toast.success({
        title: 'Simulation completed',
        body: `${c.name} · simulated run ${res.data?.id || 'SYNC-NEW'} created and audited; no external call occurred.`,
      });
      activityRail.push({
        action: 'connector.sync',
        targetType: 'connector',
        targetId: c.id,
        actorUserId: 'you',
        summary: `Simulated sync of ${c.name}; no external call`,
      });
    } catch (e) {
      toast.error({ title: 'Simulation failed', body: e.message });
    }
  }

  if (conns.loading)
    return (
      <>
        <PageHeader title="Data Integration" />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (conns.error) return <ErrorState error={conns.error} />;

  const unhealthy = connectors.filter((c) => /warn|error|fail/i.test(c.status || ''));
  const total = connectors.length;
  const readyPct = total ? Math.round((fleet.ready / total) * 1000) / 10 : null;

  const kpiItems = [
    {
      id: 'fleet-size',
      label: 'Prototype adapters',
      value: total || '—',
      icon: '🔌',
      status: 'blue',
      footer: `${fleet.ready} ready · ${fleet.degraded} degraded · ${fleet.down} down`,
    },
    {
      id: 'fleet-ready',
      label: 'Simulation readiness',
      value: readyPct == null ? '—' : readyPct,
      unit: readyPct == null ? undefined : '%',
      status: total === 0 ? 'slate' : fleet.down ? 'brick' : fleet.degraded ? 'amber' : 'sage',
      statusLabel:
        total === 0 ? undefined : fleet.down ? 'Down' : fleet.degraded ? 'Watch' : 'Green',
      footer: 'Share of prototype adapters ready for simulated diagnostics',
    },
    health.hasAvailability
      ? {
          id: 'availability',
          label: 'Projected availability (p95)',
          value: health.avgAvailabilityPct,
          unit: '%',
          status:
            health.avgAvailabilityPct >= 99
              ? 'sage'
              : health.avgAvailabilityPct >= 95
                ? 'amber'
                : 'brick',
          footer: health.hasLatency
            ? `Worst p95 latency ${health.worstLatencyMs} ms`
            : 'Simulated availability projection',
        }
      : {
          id: 'api-handoffs',
          label: 'Prototype API adapters',
          value: ecosystemSummary.find((s) => s.id === 'ecosystem-apis')?.value ?? '—',
          icon: '🔗',
          status: 'blue',
          footer: 'Simulated source-to-PRIME handoffs',
        },
    {
      id: 'open-issues',
      label: 'Prototype adapter issues',
      value: health.issuesKnown ? health.openIssues : unhealthy.length,
      icon: '⚠️',
      status: (health.issuesKnown ? health.openIssues : unhealthy.length) > 0 ? 'amber' : 'sage',
      statusLabel:
        (health.issuesKnown ? health.openIssues : unhealthy.length) > 0 ? 'Action' : 'Clear',
      footer: health.issuesKnown
        ? 'Open issues across simulated diagnostics'
        : 'Prototype adapters flagged for attention',
    },
  ];

  // Readiness gauges — only render those backed by real telemetry.
  const gauges = [];
  if (readyPct != null) {
    gauges.push({
      id: 'gauge-ready',
      value: readyPct,
      min: 0,
      max: 100,
      unit: '%',
      label: 'Simulation ready',
      sublabel: `${fleet.ready} of ${total} prototype adapters`,
      tone: fleet.down ? 'brick' : fleet.degraded ? 'amber' : 'sage',
    });
  }
  if (health.hasAvailability) {
    gauges.push({
      id: 'gauge-availability',
      value: health.avgAvailabilityPct,
      min: 90,
      max: 100,
      unit: '%',
      label: 'Projected availability',
      sublabel: 'Simulated availability signal',
      tone:
        health.avgAvailabilityPct >= 99
          ? 'sage'
          : health.avgAvailabilityPct >= 95
            ? 'amber'
            : 'brick',
    });
  }
  if (maturityPct != null) {
    gauges.push({
      id: 'gauge-maturity',
      value: maturityPct,
      min: 0,
      max: 100,
      unit: '%',
      label: 'Advanced adapter maturity',
      sublabel: 'Production-shaped prototype tier',
      tone: maturityPct >= 50 ? 'sage' : maturityPct >= 20 ? 'amber' : 'slate',
    });
  }

  return (
    <>
      <PageHeader
        eyebrow="Data & Trust"
        title="Resource ecosystem & data feeds"
        subtitle="Prototype adapter diagnostics alongside the resourcing domains used in DHA PRIME decisions."
        actions={
          <>
            <a className="btn ghost" href="/admin/lineage">
              Open resource flows
            </a>
            <a className="btn ghost" href="/admin/rbac">
              RBAC matrix
            </a>
          </>
        }
      />

      <AlertCard
        severity="blue"
        title="Prototype simulations — no external calls occur"
        body="Current connector actions validate stored adapter configuration and generate projected results only. Manual CSV/XLSX ingestion remains a separate real local file-ingestion path."
      />

      <KpiHeroBand
        eyebrow="Prototype adapter fleet"
        asOf={conns.loadedAt}
        items={kpiItems}
        columns={4}
      />

      <section
        className="card resource-ecosystem-panel di-command-panel"
        data-testid="resource-ecosystem-preview"
      >
        <div className="resource-ecosystem-head">
          <div>
            <div className="eyebrow">Resource ecosystem preview</div>
            <h2 className="card-title">EIDS resourcing and data-feed command surface</h2>
            <p className="muted">
              Licensing, infrastructure, risk, manpower, administrative routing, and contracting
              stay visible beside the data feeds that power product and portfolio decisions.
            </p>
          </div>
          <a className="btn primary sm" href="/admin/lineage">
            View flow map
          </a>
        </div>

        {total ? (
          <div className="di-fleet-row glass-panel dark">
            <div className="di-fleet-donut">
              <StatusDonutMini
                segments={donutSegments}
                size={132}
                totalLabel="Adapters"
                centerValue={total}
                ariaLabel="Prototype adapter simulation status"
              />
            </div>
            {gauges.length ? (
              <StaggerReveal className="di-gauge-grid">
                {gauges.map((g) => (
                  <GaugeCard key={g.id} {...g} />
                ))}
              </StaggerReveal>
            ) : null}
          </div>
        ) : null}

        <div className="resource-summary-grid">
          {ecosystemSummary.map((item) => (
            <article key={item.id} className={`resource-summary-card tone-${item.tone}`}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <p>{item.detail}</p>
            </article>
          ))}
        </div>
        <div className="resource-area-strip" aria-label="Resource areas">
          {RESOURCE_AREAS.map((area) => (
            <span key={area.key} className={`resource-area-pill tone-${area.tone}`}>
              <strong>{area.label}</strong>
              <small>{area.detail}</small>
            </span>
          ))}
        </div>
      </section>

      {unhealthy.length ? (
        <AlertCard
          severity="amber"
          title={`${unhealthy.length} connector${unhealthy.length === 1 ? '' : 's'} need attention`}
          body={`${unhealthy
            .map((u) => u.name)
            .slice(0, 3)
            .join(', ')}${unhealthy.length > 3 ? `, +${unhealthy.length - 3} more` : ''}`}
          actionLabel="Show unhealthy first"
          onAction={() => {
            setUnhealthyFirst(true);
            toast.show({ title: 'Sorted', body: 'Unhealthy connectors floated to the top.' });
          }}
        />
      ) : null}

      <section className="bento admin-bento">
        <div className="span-8">
          {connectors.length === 0 ? (
            <EmptyState title="No connectors" />
          ) : (
            <>
              <StaggerReveal className="admin-connector-grid di-connector-grid">
                {connectorPager.items.map((c) => (
                  <ConnectorCard
                    key={c.id}
                    connector={c}
                    syncHistory={histories[c.id]}
                    onSync={simulateSync}
                    onOpen={(cn) => navigate(`/admin/data/connectors/${cn.id}`)}
                    busy={sync.running}
                  />
                ))}
              </StaggerReveal>
              <PageViewerControls
                page={connectorPager.page}
                label="connectors"
                pageSize={connectorPager.pageSize}
                onOffsetChange={connectorPager.setOffset}
                onPageSizeChange={connectorPager.setPageSize}
              />
            </>
          )}
        </div>
        {canReadAudit ? (
        <div className="span-4 card admin-feed">
          <header>
            <div className="eyebrow">Recent activity</div>
            <h2 className="card-title">Prototype activity · data domain</h2>
          </header>
          {auditList.length === 0 ? (
            <EmptyState title="No recent audit events" />
          ) : (
            <ul className="admin-feed-list">
              {auditPager.items.map((e) => (
                <li key={e.id}>
                  <span className="admin-feed-icon" aria-hidden>
                    {/connector|sync/i.test(e.action) ? '⇄' : '◆'}
                  </span>
                  <div className="stack tight">
                    <strong>{e.action}</strong>
                    <span className="muted small">
                      <PersonLabel userId={e.actorUserId} fallback="system" /> · {e.targetType}:
                      {e.targetId}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <PageViewerControls
            page={auditPager.page}
            label="audit events"
            pageSize={auditPager.pageSize}
            onOffsetChange={auditPager.setOffset}
            onPageSizeChange={auditPager.setPageSize}
          />
        </div>
        ) : null}
      </section>
    </>
  );
}
